import { API_BASE_PATH } from '@wednesday/contracts';
import type { z } from 'zod';

import { ApiError, apiErrorFromResponse, networkError } from './errors';

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface RequestOptions<TSchema extends z.ZodType | undefined> {
  readonly body?: unknown;
  /** Response schema from @wednesday/contracts; omitted for empty (204) responses. */
  readonly schema?: TSchema;
  readonly signal?: AbortSignal;
  /** Lets the request finish after the page is closed (last-moment saves). */
  readonly keepalive?: boolean;
}

type Result<TSchema> = TSchema extends z.ZodType ? z.infer<TSchema> : undefined;

/**
 * The only way the app talks to the API (architecture §6). Same-origin requests carry the session
 * cookie; responses are validated with the shared contracts so a server/client mismatch fails loudly.
 */
export async function apiRequest<TSchema extends z.ZodType | undefined = undefined>(
  method: Method,
  path: string,
  options: RequestOptions<TSchema> = {},
): Promise<Result<TSchema>> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_PATH}${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
      ...(options.keepalive ? { keepalive: true } : {}),
    });
  } catch (error) {
    throw networkError(error);
  }

  const requestId = response.headers.get('x-request-id');
  const text = await response.text();
  const body: unknown = text === '' ? undefined : safeJson(text);

  if (!response.ok) {
    throw apiErrorFromResponse(response.status, body, requestId);
  }
  if (!options.schema) {
    return undefined as Result<TSchema>;
  }
  const parsed = options.schema.safeParse(body);
  if (!parsed.success) {
    throw new ApiError({
      status: response.status,
      code: 'CONTRACT_MISMATCH',
      title: 'The server sent data this version of the app does not understand. Reload the page.',
      requestId: requestId ?? undefined,
      details: { path, issues: parsed.error.issues.slice(0, 5) },
    });
  }
  return parsed.data as Result<TSchema>;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Uploads a file as multipart/form-data (CRD-8); the browser sets the boundary. */
export async function apiUpload<TSchema extends z.ZodType>(
  path: string,
  file: File,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  const form = new FormData();
  form.append('file', file, file.name);
  let response: Response;
  try {
    response = await fetch(`${API_BASE_PATH}${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: form,
    });
  } catch (error) {
    throw networkError(error);
  }
  const requestId = response.headers.get('x-request-id');
  const text = await response.text();
  const body: unknown = text === '' ? undefined : safeJson(text);
  if (!response.ok) throw apiErrorFromResponse(response.status, body, requestId);
  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    throw new ApiError({
      status: response.status,
      code: 'CONTRACT_MISMATCH',
      title: 'The server sent data this version of the app does not understand. Reload the page.',
      requestId: requestId ?? undefined,
      details: { path, issues: parsed.error.issues.slice(0, 5) },
    });
  }
  return parsed.data;
}
